import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  formatChinaDate,
  formatChinaDateTimeLocal,
  parseChinaDateTimeLocal,
} from '../../utils/date';

type Position = {
  _id?: string;
  title: string;
  level: 'Master' | 'PhD' | 'Postdoc' | 'Research Assistant' | 'Other';
  direction?: string;
  description?: string;
  status: 'open' | 'closed';
  deadline?: string;
};

const emptyPosition: Position = {
  title: '',
  level: 'PhD',
  direction: '',
  description: '',
  status: 'open',
  deadline: '',
};

const LEVELS: Position['level'][] = [
  'Master',
  'PhD',
  'Postdoc',
  'Research Assistant',
  'Other',
];

const AdminRecruitment: React.FC = () => {
  const [items, setItems] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Position | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPositions = async () => {
    try {
      setLoading(true);
      const res = await api.get<Position[]>('/recruitment-positions/all');
      setItems(res.data || []);
    } catch (err) {
      console.error(err);
      setError('Failed to load list.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPositions();
  }, []);

  const openCreate = () => {
    setEditing({ ...emptyPosition });
    setError(null);
  };

  const openEdit = (p: Position) => {
    setEditing({
      ...p,
      deadline: p.deadline ? formatChinaDateTimeLocal(p.deadline) : '',
    });
    setError(null);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    if (!editing) return;
    const { name, value } = e.target;
    setEditing({ ...editing, [name]: value });
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.title || !editing.level) {
      setError('Please fill in title and level.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = {
        ...editing,
        deadline: parseChinaDateTimeLocal(editing.deadline),
      };
      if (editing._id) {
        await api.put(`/recruitment-positions/${editing._id}`, payload);
      } else {
        await api.post('/recruitment-positions', payload);
      }
      setEditing(null);
      await loadPositions();
    } catch (err) {
      console.error(err);
      setError('Save failed. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p: Position) => {
    if (!p._id) return;
    if (!window.confirm(`Are you sure you want to delete "${p.title}"?`)) return;
    try {
      await api.delete(`/recruitment-positions/${p._id}`);
      await loadPositions();
    } catch (err) {
      console.error(err);
      alert('Delete failed. Please try again later.');
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          Recruitment Management
        </h1>
        <button
          onClick={openCreate}
          className="rounded-full bg-brand-red px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-white hover:bg-red-700"
        >
          Add Position
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-600">Loading...</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-subtle">
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Title
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Level
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Status
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Deadline
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p._id} className="border-b border-neutral-100">
                  <td className="max-w-xs px-3 py-2 text-neutral-900">
                    <span className="line-clamp-2">{p.title}</span>
                  </td>
                  <td className="px-3 py-2 text-neutral-700">{p.level}</td>
                  <td className="px-3 py-2 text-neutral-700">
                    {p.status === 'open' ? 'Open' : 'Closed'}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {p.deadline ? formatChinaDate(p.deadline) : '-'}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => openEdit(p)}
                      className="mr-2 text-brand-red hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      className="text-neutral-500 hover:text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-4 text-center text-neutral-500"
                  >
                    No positions yet. Click Add Position to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-subtle">
            <h2 className="mb-4 text-sm font-semibold text-neutral-900">
              {editing._id ? 'Edit Position' : 'Add Position'}
            </h2>
            <div className="space-y-3 text-xs">
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Title
                </label>
                <input
                  name="title"
                  value={editing.title}
                  onChange={handleChange}
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Recruitment level
                </label>
                <select
                  name="level"
                  value={editing.level}
                  onChange={handleChange}
                  className="um-input w-full bg-white px-2 py-1.5"
                >
                  {LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Research Direction (short)
                </label>
                <input
                  name="direction"
                  value={editing.direction || ''}
                  onChange={handleChange}
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Details (HTML supported)
                </label>
                <textarea
                  name="description"
                  value={editing.description || ''}
                  onChange={handleChange}
                  rows={5}
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block font-medium text-neutral-700">
                    Status
                  </label>
                  <select
                    name="status"
                    value={editing.status}
                    onChange={handleChange}
                    className="um-input w-full bg-white px-2 py-1.5"
                  >
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block font-medium text-neutral-700">
                    Deadline
                  </label>
                  <input
                    name="deadline"
                    type="datetime-local"
                    value={editing.deadline || ''}
                    onChange={handleChange}
                    className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                  />
                </div>
              </div>
            </div>
            {error && (
              <p className="mt-2 text-[11px] text-red-600">{error}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="rounded-full border border-neutral-300 px-4 py-1 text-neutral-700 hover:bg-neutral-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-brand-red px-4 py-1 font-semibold text-white hover:bg-red-700 disabled:opacity-70"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRecruitment;
